import { Layout, Menu, Avatar, Dropdown, Tag } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  WarningOutlined,
  AppstoreOutlined,
  GiftOutlined,
  AuditOutlined,
  LogoutOutlined,
  SettingOutlined,
  ControlOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./store";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import DApps from "./pages/DApps";
import Audit from "./pages/Audit";
import Groups from "./pages/Groups";
import RedPackets from "./pages/RedPackets";
import ModRules from "./pages/ModRules";
import SystemSettings from "./pages/Settings";

const { Header, Sider, Content } = Layout;

// 每条路由允许访问的角色集合（空集 = 所有角色）
const ROLE_MAP: Record<string, string[]> = {
  "/users":      ["super_admin", "ops_admin"],
  "/groups":     ["super_admin", "ops_admin"],
  "/reports":    ["super_admin", "ops_admin", "auditor"],
  "/modrules":   ["super_admin", "ops_admin"],
  "/dapps":      ["super_admin", "ops_admin"],
  "/redpackets": ["super_admin", "ops_admin", "finance"],
  "/settings":   ["super_admin"],
  "/audit":      ["super_admin", "ops_admin", "auditor"],
};

function canAccess(path: string, role: string | null): boolean {
  if (!role) return false;
  const allowed = ROLE_MAP[path];
  if (!allowed) return true; // 仪表盘等无限制路由
  return allowed.includes(role);
}

const allItems = [
  { key: "/", icon: <DashboardOutlined />, label: <Link to="/">仪表盘</Link> },
  { key: "/users", icon: <UserOutlined />, label: <Link to="/users">用户</Link> },
  { key: "/groups", icon: <TeamOutlined />, label: <Link to="/groups">群组</Link> },
  { key: "/reports", icon: <WarningOutlined />, label: <Link to="/reports">举报</Link> },
  { key: "/modrules", icon: <SafetyOutlined />, label: <Link to="/modrules">审核规则</Link> },
  { key: "/dapps", icon: <AppstoreOutlined />, label: <Link to="/dapps">DApp</Link> },
  { key: "/redpackets", icon: <GiftOutlined />, label: <Link to="/redpackets">红包</Link> },
  { key: "/settings", icon: <ControlOutlined />, label: <Link to="/settings">系统配置</Link> },
  { key: "/audit", icon: <AuditOutlined />, label: <Link to="/audit">操作审计</Link> },
];

export default function App() {
  const { token, role, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const menuItems = allItems.filter((item) => canAccess(item.key, role));

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider width={220} style={{ background: "#0B0D12", borderRight: "1px solid #1A1D26" }}>
        <div style={{ padding: 18, color: "#F5F5F7", fontWeight: 700, letterSpacing: 1 }}>
          QX-CHAT
          <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 400, marginTop: 4 }}>
            Admin · v0.1
          </div>
        </div>
        <Menu
          mode="inline"
          theme="dark"
          style={{ background: "transparent", borderRight: 0 }}
          selectedKeys={[loc.pathname === "/" ? "/" : "/" + loc.pathname.split("/")[1]]}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header style={{ background: "#10131A", padding: "0 24px", display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }} />
          <Tag color="purple">{role ?? "admin"}</Tag>
          <Dropdown
            menu={{
              items: [
                { key: "settings", icon: <SettingOutlined />, label: "Settings", disabled: true },
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "Sign out",
                  onClick: () => { logout(); nav("/login"); },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Avatar style={{ background: "#9B87F5", marginLeft: 12, cursor: "pointer" }}>A</Avatar>
          </Dropdown>
        </Header>
        <Content style={{ padding: 24 }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/users" element={canAccess("/users", role) ? <Users /> : <Navigate to="/" replace />} />
            <Route path="/groups" element={canAccess("/groups", role) ? <Groups /> : <Navigate to="/" replace />} />
            <Route path="/reports" element={canAccess("/reports", role) ? <Reports /> : <Navigate to="/" replace />} />
            <Route path="/modrules" element={canAccess("/modrules", role) ? <ModRules /> : <Navigate to="/" replace />} />
            <Route path="/dapps" element={canAccess("/dapps", role) ? <DApps /> : <Navigate to="/" replace />} />
            <Route path="/redpackets" element={canAccess("/redpackets", role) ? <RedPackets /> : <Navigate to="/" replace />} />
            <Route path="/settings" element={canAccess("/settings", role) ? <SystemSettings /> : <Navigate to="/" replace />} />
            <Route path="/audit" element={canAccess("/audit", role) ? <Audit /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}
